import { IReport, getMessagesWithQuestionIndex } from '../../lib/reports/base';
import { AggregatedData, getAllSubTeams } from '../../lib/reports/aggregateData';
import { TeamWithType } from '../../lib/reports/queryAggregatedData';
import { getQuestionDefinitionByIndex } from '../../lib/reportHelper';
import { flatten } from 'ramda';

type AnswerLevel = 'neutral' | 'warning' | 'alert';

export type AnswerResult = {
  answer: string;
  level: AnswerLevel;
  responses: number;
  percentage: number;
};

type QuestionResponse = {
  value?: string | null;
  respondentId?: string;
  teamId?: string;
};

type QuestionWithResponses = {
  question: NonNullable<IReport['questions']>[0];
  responses: QuestionResponse[];
  notRelevantResponses: number;
  positiveResponses: number;
  totalResponses: number;
  percentage: number;
  answers: AnswerResult[];
};

type TeamStatistics = {
  teamId: string;
  teamName: string;
  answerResults: AnswerResult[];
  notRelevantTotalCount: number;
  percentage: number;
};

/**
 * Determines the highlight level for an answer
 */
const getAnswerLevel = (highlightCount: number): AnswerLevel => {
  if (highlightCount === 0) return 'neutral';
  if (highlightCount === 1) return 'warning';
  return 'alert';
};

/**
 * Gets respondent IDs for a team including all subteams
 */
const getTeamRespondentIds = (team: TeamWithType, sectionReport: IReport, aggregatedData: AggregatedData): string[] => {
  if (team.type !== 'unit') {
    return team.team.directMembers || [];
  }

  const allSubTeams = flatten(getAllSubTeams(aggregatedData.teams, team.team));
  const subTeamsRespondentIds = allSubTeams
    .map((subTeam) => sectionReport.teams?.find((t) => t.id === subTeam)?.directMembers || [])
    .flat();

  return [...(team.team.directMembers || []), ...subTeamsRespondentIds];
};

/**
 * Calculates statistics for a single question
 */
const calculateQuestionStatistics = (
  question: NonNullable<IReport['questions']>[0],
  responses: QuestionResponse[],
): QuestionWithResponses => {
  const validResponses = responses.filter((response) => response.value && response.value.trim().length > 0);
  const totalResponses = validResponses.length;

  const positiveResponses = validResponses.filter(
    (response) => !question?.answersToHighlight?.includes(response.value ?? ''),
  ).length;

  const notRelevantResponses = responses.length - totalResponses;

  const answers: AnswerResult[] =
    question?.answersLocal?.map((answer: string, index: number) => {
      const answerResponses = validResponses.filter((response) => response.value === index.toString());
      const highlightCount =
        question?.answersToHighlight?.filter((highlight) => highlight === index.toString()).length ?? 0;

      return {
        answer,
        level: getAnswerLevel(highlightCount),
        responses: answerResponses.length,
        percentage: totalResponses === 0 ? 0 : (answerResponses.length / totalResponses) * 100,
      };
    }) ?? [];

  return {
    question,
    responses,
    notRelevantResponses,
    positiveResponses,
    totalResponses,
    percentage: totalResponses === 0 ? 0 : (positiveResponses / totalResponses) * 100,
    answers,
  };
};

/**
 * Calculates statistics for a team for a specific question
 */
export const calculateTeamQuestionStatistics = (
  question: NonNullable<IReport['questions']>[0],
  sectionReport: IReport,
  team: TeamWithType,
  aggregatedData: AggregatedData,
): QuestionWithResponses => {
  const allResponses = getMessagesWithQuestionIndex(sectionReport.responses, Number(question.index).toString());

  const teamRespondentIds = getTeamRespondentIds(team, sectionReport, aggregatedData);
  const teamResponses = allResponses.filter((response) => teamRespondentIds.includes(response.respondentId ?? ''));

  return calculateQuestionStatistics(question, teamResponses);
};

export type AggregateStatistics = {
  answers: AnswerResult[];
  averagePercentage: number;
  nonRelevantTotalCount: number;
};

/**
 * Calculates aggregate statistics across multiple questions
 */
export const calculateAggregateStatistics = (questionsWithResponses: QuestionWithResponses[]): AggregateStatistics => {
  if (questionsWithResponses.length === 0) {
    return {
      answers: [],
      averagePercentage: 0,
      nonRelevantTotalCount: 0,
    };
  }

  // Group all answers from all questions
  const groupedAnswers = questionsWithResponses.flatMap((question) => question.answers);

  // Sum up responses for each answer option
  const answerTotals = groupedAnswers.reduce(
    (acc, answer) => {
      acc[answer.answer] = (acc[answer.answer] || 0) + answer.responses;
      return acc;
    },
    {} as Record<string, number>,
  );

  const totalAnswers = Object.values(answerTotals).reduce((acc, count) => acc + count, 0);

  // Create aggregate answer results
  const answers: AnswerResult[] =
    questionsWithResponses[0]?.answers.map((answer) => ({
      level: answer.level,
      responses: answerTotals[answer.answer] ?? 0,
      percentage: totalAnswers === 0 ? 0 : (answerTotals[answer.answer] / totalAnswers) * 100,
      answer: answer.answer,
    })) ?? [];

  // Calculate average percentage across all questions
  const averagePercentage =
    questionsWithResponses.reduce((acc, question) => acc + question.percentage, 0) / questionsWithResponses.length;

  // Sum up all non-relevant responses
  const nonRelevantTotalCount = questionsWithResponses.reduce(
    (acc, question) => acc + question.notRelevantResponses,
    0,
  );

  return {
    answers,
    averagePercentage,
    nonRelevantTotalCount,
  };
};

/**
 * Calculates statistics for a single question across all responses in the report
 */
export const calculateOverallQuestionStatistics = (
  sectionReport: IReport,
  questionIndex: string,
): QuestionWithResponses => {
  const responses = getMessagesWithQuestionIndex(sectionReport.responses, questionIndex);
  const question = getQuestionDefinitionByIndex(sectionReport, questionIndex);
  return calculateQuestionStatistics(question!, responses);
};

/**
 * Checks if team statistics should be shown (has at least one non-zero answer)
 */
export const hasTeamData = (statistics: QuestionWithResponses | TeamStatistics | AggregateStatistics): boolean => {
  const answers = 'answerResults' in statistics ? statistics.answerResults : statistics.answers;
  return answers.some((answer) => answer.percentage > 0);
};
